// src/pages/cadastros/Usuarios/NovoUsuario.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTheme } from "../../../contexts/ThemeContext";
import LayoutDesktop from "../../../layouts/LayoutDesktop";
import { useAuth } from "../../../contexts/AuthContext";

import { db, getSecondaryAuth } from "../../../config/firebase";
import { createUserWithEmailAndPassword, signOut, setPersistence, inMemoryPersistence } from "firebase/auth";
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  serverTimestamp,
  where,
  runTransaction,
} from "firebase/firestore";

import { NIVEIS, LABEL_NIVEIS as NIVEL_LABEL } from "../../../config/niveis";
import { AREAS, pode, nivelUsuario } from "../../../utils/permissoes";

function traduzErro(code) {
  switch (code) {
    case "auth/email-already-in-use":
      return "Este e-mail já está em uso.";
    case "auth/invalid-email":
      return "E-mail inválido.";
    case "auth/weak-password":
      return "Senha muito fraca (mínimo 6 caracteres).";
    case "mamute/empresa-obrigatoria":
      return "Para Cliente/Kiosk, selecione exatamente 1 empresa no acesso restrito.";
    default:
      return "Erro ao criar usuário. Tente novamente.";
  }
}

// Gera próximo código sequencial DENTRO DO GRUPO
async function obterProximoCodigoSequencial(grupoId) {
  const contadorRef = doc(db, "contadores", `usuarios_${grupoId}`);

  

  // ✅ garante que o contador exista com grupoId (evita PERMISSION_DENIED no transaction.get quando o doc ainda não existe)
  await setDoc(contadorRef, { grupoId }, { merge: true });
const proximo = await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(contadorRef);

    let atual = 1;
    if (snap.exists() && snap.data().ultimoCodigoSequencial) {
      atual = snap.data().ultimoCodigoSequencial;
    }

    const novoValor = atual + 1;

    transaction.set(
      contadorRef,
      { ultimoCodigoSequencial: novoValor, grupoId },
      { merge: true }
    );

    return novoValor;
  });

  return proximo;
}

function NovoUsuario() {
  const { tema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();

  // dados básicos
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [senha, setSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [corAgenda, setCorAgenda] = useState("#3b82f6");

  // nível
  const [nivel, setNivel] = useState("");

  // Áreas de acesso (domínios do sistema)
  const [areas, setAreas] = useState({
    [AREAS.MANUTENCAO]: false,
    [AREAS.FINANCEIRO]: false,
  });

  // grupo / empresa (puxa como padrão o que está no usuário logado)
  const [grupoId, setGrupoId] = useState(usuarioLogado?.grupoId || "");
  const [empresaBase, setEmpresaBase] = useState(usuarioLogado?.empresa || "");

  // acesso
  const [tipoAcesso, setTipoAcesso] = useState("GLOBAL"); // GLOBAL | RESTRITO
  const [empresasTexto, setEmpresasTexto] = useState("");

// Empresas do grupo para seleção (RESTRITO)
const [empresasDisponiveis, setEmpresasDisponiveis] = useState([]);
const [empresasSelecionadas, setEmpresasSelecionadas] = useState([]);
const [buscaEmpresa, setBuscaEmpresa] = useState("");

useEffect(() => {
  async function carregarEmpresas() {
    try {
      const gid = (grupoId || "").trim();
      if (!gid) {
        setEmpresasDisponiveis([]);
        return;
      }
      const qEmp = query(collection(db, "empresas"), where("grupoId", "==", gid));
      const snap = await getDocs(qEmp);
      const lista = snap.docs
        .map((d) => ({ id: d.id, nome: d.data()?.nome || "" }))
        .filter((e) => e.nome)
        .sort((a, b) => a.nome.localeCompare(b.nome));
      setEmpresasDisponiveis(lista);
    } catch (e) {
      console.error("Erro ao carregar empresas:", e);
      setEmpresasDisponiveis([]);
    }
  }
  carregarEmpresas();
}, [grupoId]);

useEffect(() => {
  if (tipoAcesso === "GLOBAL") {
    setEmpresasSelecionadas([]);
    setBuscaEmpresa("");
  }
}, [tipoAcesso]);

const empresasFiltradas = useMemo(() => {
  const t = (buscaEmpresa || "").trim().toLowerCase();
  if (!t) return empresasDisponiveis;
  return empresasDisponiveis.filter((e) => e.nome.toLowerCase().includes(t));
}, [buscaEmpresa, empresasDisponiveis]);

function toggleEmpresa(emp) {
  setEmpresasSelecionadas((prev) => {
    const existe = prev.some((p) => p.id === emp.id);
    if (existe) return prev.filter((p) => p.id !== emp.id);
    return [...prev, { id: emp.id, nome: emp.nome }];
  });
}


  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [carregando, setCarregando] = useState(false);

  const opcoesNivelBase = [
    { value: NIVEIS.DASHBOARD, label: NIVEL_LABEL[NIVEIS.DASHBOARD] },
    { value: NIVEIS.KIOSK, label: NIVEL_LABEL[NIVEIS.KIOSK] },
    { value: NIVEIS.CLIENTE, label: NIVEL_LABEL[NIVEIS.CLIENTE] },
    { value: NIVEIS.TECNICO, label: NIVEL_LABEL[NIVEIS.TECNICO] },
    { value: NIVEIS.TECNICO_SENIOR, label: NIVEL_LABEL[NIVEIS.TECNICO_SENIOR] },
    { value: NIVEIS.ADMIN, label: NIVEL_LABEL[NIVEIS.ADMIN] },
    { value: NIVEIS.GESTOR, label: NIVEL_LABEL[NIVEIS.GESTOR] },
  ];

  const nivelEditor = nivelUsuario(usuarioLogado);
  const podeCriarUsuario = pode(usuarioLogado, "cadastros", "usuarios", "form");

  const opcoesNivel = useMemo(() => {
    // Regra: Administrador (5) cria somente níveis abaixo dele (0–4)
    if (nivelEditor === NIVEIS.ADMIN) {
      return opcoesNivelBase.filter((n) => n.value < NIVEIS.ADMIN);
    }
    // Gestor pode criar qualquer nível
    if (nivelEditor === NIVEIS.GESTOR) return opcoesNivelBase;
    // Outros níveis: por padrão, não liberamos criação
    return opcoesNivelBase.filter(() => false);
  }, [nivelEditor]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");
    setSucesso("");

    if (!podeCriarUsuario) {
      setErro("Você não tem permissão para criar usuários.");
      return;
    }

    // Regra: ADM não pode criar Admin/Gestor
    if (nivelEditor === NIVEIS.ADMIN && Number(nivel) >= NIVEIS.ADMIN) {
      setErro("Administrador só pode criar usuários com nível abaixo de Administrador (0–4). ");
      return;
    }

    if (!nome.trim()) {
      setErro("Informe o nome do usuário.");
      return;
    }

    if (!email.trim()) {
      setErro("Informe o e-mail do usuário.");
      return;
    }

    if (nivel === "" || nivel === null || typeof nivel === "undefined") {
      setErro("Selecione o nível de acesso.");
      return;
    }

    if (senha !== confirmarSenha) {
      setErro("As senhas não conferem.");
      return;
    }

    if (senha.length < 6) {
      setErro("A senha deve ter pelo menos 6 caracteres.");
      return;
    }

    const temAlgumaArea = Object.values(areas || {}).some(Boolean);
    if (!temAlgumaArea) {
      setErro("Selecione pelo menos uma área de acesso.");
      return;
    }

    const grupoAtual = (usuarioLogado?.grupoId || "").trim();
    if (!grupoAtual) {
      setErro(
        "Usuário logado não possui grupo definido. Verifique o cadastro do Gestor."
      );
      return;
    }
    const empresaBaseAtual = (usuarioLogado?.empresa || empresaBase || "").trim();
    if (!empresaBaseAtual) {
      setErro("Usuário logado não possui empresa base definida. Verifique o cadastro do Gestor.");
      return;
    }

    setCarregando(true);

    try {
      // 1) Próximo código sequencial para ESTE grupo
      const sequencial = await obterProximoCodigoSequencial(grupoAtual);
      const codigo = `US${String(sequencial).padStart(4, "0")}`;

      // 2) Cria usuário no Auth SEM deslogar o admin (usa Auth secundário)
      const secondaryAuth = getSecondaryAuth();
      await setPersistence(secondaryAuth, inMemoryPersistence);
      const cred = await createUserWithEmailAndPassword(secondaryAuth, email, senha);

      // 3) Trata empresas de acesso
      const globalAccess = tipoAcesso === "GLOBAL";
      const empresasPermitidas = globalAccess
        ? []
        : empresasSelecionadas
            .map((e) => String(e.id || "").trim())
            .filter(Boolean);

      // (opcional) detalhes só para UI/relatórios
      const empresasPermitidasDetalhes = globalAccess
        ? []
        : empresasSelecionadas
            .map((e) => ({ id: String(e.id || "").trim(), nome: String(e.nome || "").trim() }))
            .filter((e) => e.id);

      // 3.1) Define empresa padrão do usuário (para níveis baixos / escopo fixo)
      // - Se for RESTRITO e tiver exatamente 1 empresa marcada, usamos ela como empresaId/empresaNome.
      // - Mantém compatibilidade com o campo antigo "empresa" (nome).
      let empresaIdEfetivo = null;
      let empresaNomeEfetivo = empresaBaseAtual;

      if (!globalAccess && empresasSelecionadas.length === 1) {
        const e0 = empresasSelecionadas[0] || {};
        empresaIdEfetivo = String(e0.id || "").trim() || null;
        empresaNomeEfetivo = String(e0.nome || "").trim() || empresaNomeEfetivo;
      }

      // Regra de segurança: Cliente/Kiosk/Dashboard precisam estar fixos em 1 empresa
      if (Number(nivel) <= NIVEIS.CLIENTE && !empresaIdEfetivo) {
        throw { code: "mamute/empresa-obrigatoria" };
      }

      // 4) Grava no Firestore
      await setDoc(doc(db, "usuarios", cred.user.uid), {
        nome: nome.trim(),
        email: email.trim(),
        telefone: telefone.trim() || null,

        nivel: Number(nivel),
        codigoSequencial: sequencial,
        codigo,

        grupoId: grupoAtual,
        // Compatibilidade (nome): usado em versões antigas e como fallback
        empresa: empresaNomeEfetivo,
        // Preferência (id + nome): usado para filtros e escopo (EmpresaContext)
        empresaId: empresaIdEfetivo,
        empresaNome: empresaNomeEfetivo,
        globalAccess,
        tipoAcesso,
        empresasPermitidas,
        empresasPermitidasDetalhes,
        // Domínios do sistema
        areas,
        corAgenda: (corAgenda || "#3b82f6").toLowerCase(),

        criadoPor: usuarioLogado?.uid || null,
        criadoEm: serverTimestamp(),
      });

      // encerra sessão do auth secundário (não afeta o admin)
      await signOut(secondaryAuth);

      setSucesso(`Usuário criado com sucesso (código ${codigo}).`);
      setNome("");
      setEmail("");
      setTelefone("");
      setSenha("");
      setConfirmarSenha("");
      setNivel("");
      setEmpresasTexto("");
      setEmpresasSelecionadas([]);
      setBuscaEmpresa("");
      setCorAgenda("#3b82f6");
      setAreas({
        [AREAS.MANUTENCAO]: false,
        [AREAS.FINANCEIRO]: false,
      });
    } catch (err) {
      console.error(err);
      setErro(traduzErro(err.code));
    } finally {
      setCarregando(false);
    }
  };

  if (!podeCriarUsuario) {
    return (
      <LayoutDesktop
        title="Novo Usuário"
        usuario={usuarioLogado?.email || "Usuário"}
        empresa={usuarioLogado?.empresa || "-"}
      >
        <div style={{ padding: 16 }}>
          <div style={{ padding: 16, borderRadius: 12, background: "#111827", color: "#fff" }}>
            <h2 style={{ margin: 0, fontSize: 18 }}>Acesso negado</h2>
            <p style={{ marginTop: 8, opacity: 0.9 }}>
              Seu perfil não tem permissão para criar usuários.
            </p>
            <button
              onClick={() => navigate(-1)}
              style={{ marginTop: 12, padding: "10px 14px", borderRadius: 10, border: "none", cursor: "pointer" }}
            >
              Voltar
            </button>
          </div>
        </div>
      </LayoutDesktop>
    );
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Novo Usuário"
      subtitulo="Cadastros / Usuários / Novo"
    >
      <section style={{ width: "100%" }}>
        <div
          className="pc-card"
          style={{
            width: "100%",
            paddingLeft: 40,
            paddingRight: 40,
            boxSizing: "border-box",
          }}
        >
          <header className="pc-table-header">
            <div className="pc-table-header-text">
              <h3>Cadastro de usuário</h3>
              <p>
                Crie usuários vinculados ao grupo/empresa, com nível de acesso
                e permissões básicas.
              </p>
            </div>
          </header>

          {erro && (
            <div
              style={{
                marginTop: 12,
                padding: 8,
                borderRadius: 8,
                background: "#7f1d1d",
                color: "#fecaca",
                fontSize: 14,
              }}
            >
              {erro}
            </div>
          )}

          {sucesso && (
            <div
              style={{
                marginTop: 12,
                padding: 8,
                borderRadius: 8,
                background: "#14532d",
                color: "#bbf7d0",
                fontSize: 14,
              }}
            >
              {sucesso}
            </div>
          )}

          <form
            autoComplete="off"
            onSubmit={handleSubmit}
            style={{
              marginTop: 12,
              maxWidth: 650,
              marginLeft: "auto",
              marginRight: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {/* NOME */}
            <label>
              <span style={{ fontSize: 14 }}>Nome</span>
              <input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  
                }}
              />
            </label>

            {/* EMAIL */}
            <label>
              <span style={{ fontSize: 14 }}>E-mail</span>
              <input
                type="email"
                name="novo_usuario_email"
                autoComplete="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  
                }}
              />
            </label>

            {/* TELEFONE */}
            <label>
              <span style={{ fontSize: 14 }}>Telefone</span>
              <input
                type="tel"
                value={telefone}
                onChange={(e) => setTelefone(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  
                }}
              />
            </label>

            {/* NÍVEL */}
            <label>
              <span style={{ fontSize: 14 }}>Nível de acesso</span>
              <select
                value={nivel === "" ? "" : String(nivel)}
                onChange={(e) =>
                  setNivel(e.target.value === "" ? "" : Number(e.target.value))
                }
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  
                  color: "inherit",
                }}
              >
                <option value="" disabled>
                  Selecione...
                </option>
                {opcoesNivel.map((n) => (
                  <option key={n.value} value={n.value}>
                    {n.label}
                  </option>
                ))}
              </select>
            </label>

            {/* COR NO CALENDARIO */}
            <label>
              <span style={{ fontSize: 14 }}>Cor no calendario</span>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4 }}>
                <input
                  type="color"
                  value={corAgenda}
                  onChange={(e) => setCorAgenda(e.target.value)}
                  style={{
                    width: 48,
                    height: 34,
                    padding: 0,
                    borderRadius: 8,
                    border: "1px solid #334155",
                    background: "transparent",
                  }}
                />
                <input
                  type="text"
                  value={corAgenda}
                  onChange={(e) => setCorAgenda(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "9px 11px",
                    borderRadius: 8,
                    border: "1px solid #334155",
                  }}
                />
              </div>
            </label>

            {/* ÁREAS */}
            <div>
              <span style={{ fontSize: 14 }}>Áreas do sistema</span>
              <div
                style={{
                  marginTop: 4,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  fontSize: 14,
                }}
              >
                <label>
                  <input
                    type="checkbox"
                    checked={!!areas[AREAS.MANUTENCAO]}
                    onChange={(e) =>
                      setAreas((prev) => ({ ...prev, [AREAS.MANUTENCAO]: e.target.checked }))
                    }
                    style={{ marginRight: 6 }}
                  />
                  Manutenção
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={!!areas[AREAS.FINANCEIRO]}
                    onChange={(e) =>
                      setAreas((prev) => ({ ...prev, [AREAS.FINANCEIRO]: e.target.checked }))
                    }
                    style={{ marginRight: 6 }}
                  />
                  Financeiro
                </label>
              </div>
              <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
                Dica: marque as áreas que esse usuário poderá acessar no menu.
              </div>
            </div>

                        {/* ACESSO EMPRESAS */}
            <div>
              <span style={{ fontSize: 14 }}>Tipo de acesso</span>
              <div
                style={{
                  marginTop: 4,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                  fontSize: 14,
                }}
              >
                <label>
                  <input
                    type="radio"
                    name="tipoAcesso"
                    value="GLOBAL"
                    checked={tipoAcesso === "GLOBAL"}
                    onChange={() => setTipoAcesso("GLOBAL")}
                    style={{ marginRight: 6 }}
                  />
                  Global (todas as empresas do grupo)
                </label>
                <label>
                  <input
                    type="radio"
                    name="tipoAcesso"
                    value="RESTRITO"
                    checked={tipoAcesso === "RESTRITO"}
                    onChange={() => setTipoAcesso("RESTRITO")}
                    style={{ marginRight: 6 }}
                  />
                  Restrito a empresas específicas
                </label>
              </div>
            </div>

            {tipoAcesso === "RESTRITO" && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 14, marginBottom: 6 }}>Empresas com acesso</div>

                <input
                  type="text"
                  value={buscaEmpresa}
                  onChange={(e) => setBuscaEmpresa(e.target.value)}
                  placeholder="Buscar empresa..."
                  style={{
                    width: "100%",
                    padding: 10,
                    borderRadius: 10,
                    border: "1px solid var(--cor-borda-input, rgba(255,255,255,0.12))",
                    background: "var(--cor-input-bg, var(--cor-card, rgba(255,255,255,0.04)))",
                    color: "var(--cor-input-texto, inherit)",
                    marginBottom: 10,
                  }}
                />

                <div
                  style={{
                    border: "1px solid var(--cor-borda-input, rgba(255,255,255,0.12))",
                    borderRadius: 12,
                    padding: 10,
                    maxHeight: 220,
                    overflow: "auto",
                    background: "var(--cor-card, rgba(255,255,255,0.04))",
                  }}
                >
                  {empresasFiltradas.length === 0 ? (
                    <div style={{ fontSize: 13, opacity: 0.7 }}>Nenhuma empresa encontrada.</div>
                  ) : (
                    empresasFiltradas.map((emp) => {
                      const checked = empresasSelecionadas.some((e) => e.id === emp.id);
                      return (
                        <label
                          key={emp.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "6px 4px",
                            cursor: "pointer",
                          }}
                        >
                          <input type="checkbox" checked={checked} onChange={() => toggleEmpresa(emp)} />
                          <span style={{ fontSize: 14 }}>{emp.nome}</span>
                        </label>
                      );
                    })
                  )}
                </div>

                <div style={{ fontSize: 12, opacity: 0.75, marginTop: 8 }}>
                  Dica: no acesso restrito, marque apenas as empresas que esse usuário pode acessar.
                  Em modo global, ele vê tudo do grupo.
                </div>
              </div>
            )}

            {/* SENHA */}
            <label>
              <span style={{ fontSize: 14 }}>Senha inicial</span>
              <input
                type="password"
                name="novo_usuario_senha"
                autoComplete="new-password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  
                }}
              />
            </label>

            <label>
              <span style={{ fontSize: 14 }}>Confirmar senha</span>
              <input
                type="password"
                name="novo_usuario_confirmar_senha"
                autoComplete="new-password"
                value={confirmarSenha}
                onChange={(e) => setConfirmarSenha(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "9px 11px",
                  marginTop: 4,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  
                }}
              />
            </label>

            {/* AÇÕES */}
            <div
              style={{
                marginTop: 16,
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={() => navigate("/cadastros/usuarios")}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="pc-btn-primario"
                disabled={carregando}
              >
                {carregando ? "Salvando..." : "Salvar usuário"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default NovoUsuario;
